import { Status, type StatusResponse } from '@status/shared';
import { formatClock } from '../lib/format';

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone?: 'ok' | 'warn' | 'down';
}): React.JSX.Element {
  const toneClass =
    tone === 'ok'
      ? 'text-emerald-600 dark:text-emerald-400'
      : tone === 'warn'
        ? 'text-amber-600 dark:text-amber-400'
        : tone === 'down'
          ? 'text-rose-600 dark:text-rose-400'
          : '';

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-800 dark:bg-slate-900">
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
      <div className={`text-xl font-semibold tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );
}

export function Summary({ data }: { data: StatusResponse }): React.JSX.Element {
  const layers = data.groups.flatMap((g) => g.layers);
  const probed = layers.filter((l) => l.current_status.timestamp > 0);

  const up = probed.filter((l) => l.current_status.status === Status.AVAILABLE).length;
  const degraded = probed.filter((l) => l.current_status.status === Status.DEGRADED).length;
  const down = probed.filter((l) => l.current_status.status === Status.UNAVAILABLE).length;

  const headline =
    probed.length === 0
      ? '等待首次探测'
      : down > 0
        ? `${down} 个模型不可用`
        : degraded > 0
          ? `${degraded} 个模型降级`
          : '全部模型正常';

  const headlineTone =
    probed.length === 0
      ? 'text-slate-500 dark:text-slate-400'
      : down > 0
        ? 'text-rose-600 dark:text-rose-400'
        : degraded > 0
          ? 'text-amber-600 dark:text-amber-400'
          : 'text-emerald-600 dark:text-emerald-400';

  return (
    <div className="space-y-3">
      <p className={`text-lg font-semibold ${headlineTone}`} role="status">
        {headline}
      </p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Tile label="正常" value={up} tone="ok" />
        <Tile label="降级" value={degraded} tone="warn" />
        <Tile label="故障" value={down} tone="down" />
        <Tile label="监控模型" value={layers.length} />
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        最后更新 {formatClock(data.meta.generated_at)}
        {data.meta.next_probe_at !== null && (
          <> · 下次探测 {formatClock(data.meta.next_probe_at)}</>
        )}
      </p>
    </div>
  );
}
